import React, { useCallback, useMemo, useRef } from 'react'

import classnames from 'classnames'

import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'

import Image from 'shared/components/common/Image'
import { ToggleInput } from 'shared/components/common/forms'

import QuantitySelector from 'public/components/default_template/online_ordering/item_modal/QuantitySelector'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { Modifier } from 'public/components/online_ordering/types'
import { t } from 'src/translations'
import { formatImageURL } from 'src/lib/js/utilities'
import { PlaceSettingIcon, AllergenIcon } from '@toasttab/buffet-pui-icons'
import {
  getModifierThumbSrc,
  getSubselectionNames
} from 'public/components/online_ordering/modifierUtils'
import { alertWarning } from 'src/shared/js/alertUtils'
import { useAllergenSheet } from './AllergenSheetContext'

type RowProps = {
  modifier: Modifier
  groupGuid: string
  inputType: 'radio' | 'checkbox'
  disabled?: boolean
  canAddMore?: boolean
  autoFocus?: boolean
  groupHasImages?: boolean
}

const SelectionRow = ({
  groupGuid,
  modifier,
  inputType,
  disabled,
  canAddMore,
  autoFocus,
  groupHasImages
}: RowProps) => {
  const onViewAllergens = useAllergenSheet()
  const toggleInputRef = useRef<HTMLInputElement>()
  const hasModifierGroups = modifier.modifierGroups?.length
  const {
    pushModifier,
    displayedModifier,
    select,
    deselect,
    modifierGroupData,
    modifierAllergenMap
  } = useModifierContext()
  const selectedItem =
    displayedModifier?.modifierGroups[groupGuid]?.[modifier.itemGuid]
  const selected = !!selectedItem
  const thisPrice = modifier.price
  const selectAction = inputType === 'radio' ? 'replace' : 'merge'
  const name = modifier.name || undefined
  const subSelectionNames = displayedModifier
    ? getSubselectionNames(displayedModifier, groupGuid, modifier.itemGuid)
    : []
  const isIncludedDefault =
    modifier.isDefault &&
    modifierGroupData[groupGuid]?.defaultOptionsChargePrice === 'NO'
  const shouldShowPrice =
    modifier.price &&
    modifier.price !== 0 &&
    (!isIncludedDefault || (selectedItem && selectedItem.quantity > 1))
  const shouldShowCalories =
    modifier.calories !== null && modifier.calories !== undefined
  const modDisabled = useMemo(
    () => disabled || !!modifier.outOfStock,
    [disabled, modifier.outOfStock]
  )

  const onToggle = useCallback(() => {
    if (selected && inputType === 'checkbox') {
      deselect(groupGuid, modifier.itemGuid)
    } else {
      select(
        groupGuid,
        modifier.itemGuid,
        1,
        selectAction,
        modifier.itemGroupGuid,
        name,
        thisPrice,
        modifier.isDefault
      )

      // Show allergen alert if modifier has allergens
      const modifierData = modifierAllergenMap?.[modifier.itemGuid]
      if (modifierData?.allergens?.length) {
        const allergenNames = modifierData.allergens
          .filter(Boolean)
          .map((a) => t(`allergens.${a!.code}`, a!.code))
          .join(', ')

        alertWarning(
          t('allergen-info.added-alert', {
            allergenNames
          }),
          {
            containerId: 'itemModalToastContainer',
            toastId: modifier.itemGuid,
            ...(onViewAllergens && {
              closeButton: ({ closeToast }) => (
                <button
                  className='toastViewButton'
                  onClick={(e) => {
                    closeToast(e)
                    onViewAllergens()
                  }}
                >
                  {t('allergen-info.view-button')}
                </button>
              )
            })
          }
        )
      }
    }

    if (!thisPrice) {
      return
    }
  }, [
    selected,
    inputType,
    name,
    selectAction,
    deselect,
    groupGuid,
    select,
    thisPrice,
    modifier,
    modifierAllergenMap,
    onViewAllergens
  ])

  const onQuantityChange = useCallback(
    (quantity: number, _: number, defaultValueChange?: boolean) => {
      if (defaultValueChange) {
        return
      }

      select(
        groupGuid,
        modifier.itemGuid,
        quantity,
        selectAction,
        modifier.itemGroupGuid,
        name,
        thisPrice,
        modifier.isDefault
      )
    },
    [thisPrice, name, groupGuid, modifier, select, selectAction]
  )

  const modifierId = `${groupGuid}_${modifier.itemGuid}`
  const expandId = `${modifierId}-expand`
  const showQuantitySelector =
    selected && modifier.allowsDuplicates && !hasModifierGroups
  const modifierThumbSrc = getModifierThumbSrc(modifier)
  const modifierThumbUrl = modifierThumbSrc
    ? formatImageURL(modifierThumbSrc)
    : null
  return (
    <div
      className={`row ${hasModifierGroups ? 'subGroups' : ''}`}
      key={modifier.itemGuid}
      id='row'
      data-testid={'mod-' + modifier.itemGuid}
      onClick={(e) => {
        if (hasModifierGroups && !modDisabled) {
          e.stopPropagation()
          e.preventDefault()
          pushModifier(groupGuid, modifier, selectAction)
        }
      }}
    >
      <ToggleInput
        ref={toggleInputRef}
        type={inputType}
        name={name ? `${groupGuid}-${name}` : groupGuid}
        id={modifierId}
        dataTestId={`${modifierId}-input`}
        autoFocus={autoFocus}
        disabled={modDisabled}
        checked={selected}
        onChange={(e) => {
          if (hasModifierGroups) {
            e.stopPropagation()
            e.preventDefault()
          } else if (
            !selectedItem ||
            selectedItem.quantity < 1 ||
            !modifier.allowsDuplicates
          ) {
            onToggle()
          }
        }}
        labelClassName={
          showQuantitySelector ? 'hasQuantitySelector' : undefined
        }
        textOnly={showQuantitySelector ?? false}
        ariaLabelledBy={`${modifierId}-label`}
        ariaDescribedBy={`${modifierId}-expand`}
        afterLabelContent={
          <>
            {showQuantitySelector ? (
              <div
                className='actions'
                id={expandId}
                data-testid={`${expandId}-quantity-selector`}
              >
                <QuantitySelector
                  allowDelete
                  className='rowSelector ooQuantitySelector'
                  onDelete={onToggle}
                  min={1}
                  canIncrease={canAddMore}
                  defaultValue={selectedItem?.quantity || 1}
                  onQuantityChange={onQuantityChange}
                />
              </div>
            ) : null}
            {hasModifierGroups ? (
              <button
                type='button'
                className={classnames('actions', { disabled: modDisabled })}
                id={expandId}
                data-testid={`${expandId}-caret`}
                disabled={modDisabled}
                aria-label={t('selection-row.alt.click')}
                onClick={(e) => {
                  if (!modDisabled) {
                    e.stopPropagation()
                    pushModifier(groupGuid, modifier, selectAction)
                  }
                }}
              >
                <Image alt='' src='/icons/caret-right.svg' />
              </button>
            ) : null}
          </>
        }
      >
        <div
          className={classnames('toggleContents', {
            hasModifiers: hasModifierGroups,
            selectedWithQuantity: selected && modifier.allowsDuplicates
          })}
        >
          <div
            className={classnames('modifierTextContent', {
              leftAligned: !modifier.price || modifier.price === 0
            })}
            id={`${modifierId}-label`}
            data-testid={`${modifierId}-label`}
          >
            <div className='modifierText'>
              {modifierThumbUrl ? (
                <img
                  className='modifierThumb'
                  src={modifierThumbUrl}
                  alt=''
                  aria-hidden='true'
                />
              ) : groupHasImages ? (
                <div
                  className='modifierThumb modifierThumbPlaceholder'
                  aria-hidden='true'
                >
                  <PlaceSettingIcon accessibility='decorative' />
                </div>
              ) : null}
              <div className='modifierNameAndPrice'>
                <div
                  className={`name ${
                    selected ? 'selected' : modDisabled ? 'disabled' : ''
                  }`}
                >
                  {modifier.name}
                  {modifierAllergenMap?.[modifier.itemGuid]?.allergens
                    ?.length ? (
                    <>
                      <AllergenIcon size='xs' accessibility='decorative' />
                      <span className='screenReaderOnly'>
                        {t('allergen-info.modifier-contains-allergens')}
                      </span>
                    </>
                  ) : null}
                </div>
                {(shouldShowPrice || shouldShowCalories) && (
                  <div className='priceAndCals'>
                    {shouldShowPrice && (
                      <span
                        className={`price ${
                          selected ? 'selected' : modDisabled ? 'disabled' : ''
                        }`}
                      >
                        {(modifier.price ?? 0) > 0 ? '+' : '-'}
                        <FormattedPrice
                          value={
                            (selected
                              ? isIncludedDefault
                                ? selectedItem.quantity - 1
                                : selectedItem.quantity
                              : 1) * Math.abs(modifier.price ?? 0)
                          }
                        />
                      </span>
                    )}
                    {shouldShowPrice && shouldShowCalories && (
                      <span aria-hidden={true}>•</span>
                    )}
                    {shouldShowCalories && (
                      <div className='calories'>
                        {t('item-modal.calories', {
                          calories: modifier.calories
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ToggleInput>
      {subSelectionNames.length > 0 && (
        <ul className='nestedSelections'>
          <li className='selectionName'>{subSelectionNames.join(', ')}</li>
        </ul>
      )}
    </div>
  )
}

export default SelectionRow
